import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Box,
  VStack,
  Text,
  Heading,
  Button,
  Input,
  Wrap,
  WrapItem
} from '@chakra-ui/react';

const IntentChipsScreen = ({ 
  onContinue, 
  onSkip, 
  initialData = null 
}) => {
  const [selectedVibes, setSelectedVibes] = useState([]);
  const [selectedOccasion, setSelectedOccasion] = useState(null);
  const [selectedAesthetics, setSelectedAesthetics] = useState([]);
  const [customVibe, setCustomVibe] = useState('');
  const [customOccasion, setCustomOccasion] = useState('');
  const [customAesthetic, setCustomAesthetic] = useState('');
  const [showCustomVibe, setShowCustomVibe] = useState(false);
  const [showCustomOccasion, setShowCustomOccasion] = useState(false);
  const [showCustomAesthetic, setShowCustomAesthetic] = useState(false);

  // Initialize state with saved data when component mounts or initialData changes
  useEffect(() => {
    if (initialData) {
      setSelectedVibes(initialData.vibes || []);
      setSelectedOccasion(initialData.occasion || null);
      setSelectedAesthetics(initialData.aesthetics || []);
      
      // Handle custom vibes
      const customVibes = initialData.customVibes || [];
      if (customVibes.length > 0) {
        setCustomVibe(customVibes[0]); // Take first custom vibe
        setShowCustomVibe(true);
      }
      
      // Handle custom occasion
      if (initialData.customOccasion) {
        setCustomOccasion(initialData.customOccasion);
        setShowCustomOccasion(true);
      }
      
      // Handle custom aesthetic
      if (initialData.customAesthetic) {
        setCustomAesthetic(initialData.customAesthetic);
        setShowCustomAesthetic(true);
      }
    }
  }, [initialData]);

  const vibeOptions = [
    'Confident', 'Soft', 'Sexy', 'Cool', 'Chic'
  ];

  const occasionOptions = [
    'Work', 'Date', 'Party', 'Casual Day', 'Night Out', 
    'Travel', 'Brunch'
  ];

  const aestheticOptions = [
    'Minimal', 'Elevated', 'Street', 'Earthy', 'Clean Classic', 'Soft Luxe'
  ];

  const handleVibeSelect = (vibe) => {
    if (selectedVibes.includes(vibe)) {
      setSelectedVibes(selectedVibes.filter(v => v !== vibe));
    } else if (selectedVibes.length < 2) {
      setSelectedVibes([...selectedVibes, vibe]);
    }
  };

  const handleOccasionSelect = (occasion) => {
    setSelectedOccasion(occasion === selectedOccasion ? null : occasion);
  };

  const handleAestheticSelect = (aesthetic) => {
    if (selectedAesthetics.includes(aesthetic)) {
      setSelectedAesthetics(selectedAesthetics.filter(a => a !== aesthetic));
    } else {
      setSelectedAesthetics([...selectedAesthetics, aesthetic]);
    }
  };

  const handleContinue = () => {
    const chipData = {
      vibes: selectedVibes,
      occasion: selectedOccasion,
      aesthetics: selectedAesthetics,
      customVibes: customVibe ? [customVibe] : [],
      customOccasion: customOccasion || '',
      customAesthetic: customAesthetic || ''
    };
    onContinue(chipData);
  };

  const ChipButton = ({ label, isSelected, onClick, maxReached = false }) => (
    <Button
      size="sm"
      variant="outline"
      borderRadius="full"
      px={4}
      py={2}
      onClick={onClick}
      bg={isSelected ? "blue.500" : "white"}
      color={isSelected ? "white" : "gray.700"}
      borderColor={isSelected ? "blue.500" : "gray.300"}
      _hover={!maxReached ? {
        borderColor: "blue.400",
        bg: isSelected ? "blue.600" : "blue.50"
      } : {}}
      _disabled={{
        opacity: 0.5,
        cursor: "not-allowed"
      }}
      isDisabled={maxReached && !isSelected}
      transition="all 0.2s"
    >
      {label}
    </Button>
  );

  return (
    <Box className="bg-pink-950" minH="100vh">
      <VStack spacing={0} align="stretch" maxW="4xl" mx="auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeInOut" }}
          className="w-full"
        >
          <Box className="bg-gradient-to-br from-pink-50 to-rose-50 backdrop-blur-sm rounded-3xl shadow-2xl mx-4 md:mx-8 overflow-hidden border border-orange-100/30 my-8">
            <VStack 
              spacing={8} 
              p={{ base: 8, md: 12 }} 
              align="stretch"
              className="bg-gradient-to-br from-orange-50/50 to-orange-100/30"
            >
              {/* Header */}
              <VStack spacing={2}>
                <Heading size="lg" textAlign="center" color="gray.800">
                  Tell me a bit about what you want today
                </Heading>
                <Text fontSize="md" color="gray.600" textAlign="center">
                  (optional)
                </Text>
                <Text fontSize="sm" color="gray.500" textAlign="center">
                  No pressure. No mandatory fields.
                </Text>
              </VStack>

              {/* Section A: Aesthetic Chips */}
              <VStack spacing={4} align="stretch">
                <Text fontSize="lg" fontWeight="semibold" color="gray.700">
                  Which aesthetic feels closest?
                </Text>
                <Wrap spacing={3}>
                  {aestheticOptions.map((aesthetic) => (
                    <WrapItem key={aesthetic}>
                      <ChipButton
                        label={aesthetic}
                        isSelected={selectedAesthetics.includes(aesthetic)}
                        onClick={() => handleAestheticSelect(aesthetic)}
                      />
                    </WrapItem>
                  ))}
                  <WrapItem>
                    {!showCustomAesthetic ? (
                      <Button
                        size="sm"
                        variant="outline"
                        borderRadius="full"
                        px={4}
                        py={2}
                        onClick={() => setShowCustomAesthetic(true)}
                        borderColor="blue.300"
                        color="blue.600"
                        borderStyle="dashed"
                        _hover={{ borderColor: "blue.400", bg: "blue.50" }}
                      >
                        + Describe my aesthetic
                      </Button>
                    ) : (
                      <Input
                        placeholder="Describe your aesthetic..."
                        size="sm"
                        borderRadius="full"
                        value={customAesthetic}
                        onChange={(e) => setCustomAesthetic(e.target.value)}
                        onBlur={() => !customAesthetic && setShowCustomAesthetic(false)}
                        autoFocus
                        maxW="250px"
                      />
                    )}
                  </WrapItem>
                </Wrap>
              </VStack>

              {/* Section B: Vibe Chips */}
              <VStack spacing={4} align="stretch">
                <Text fontSize="lg" fontWeight="semibold" color="gray.700">
                  Your vibe today:
                </Text>
                <Wrap spacing={3}>
                  {vibeOptions.map((vibe) => (
                    <WrapItem key={vibe}>
                      <ChipButton
                        label={vibe}
                        isSelected={selectedVibes.includes(vibe)}
                        onClick={() => handleVibeSelect(vibe)}
                        maxReached={selectedVibes.length >= 2}
                      />
                    </WrapItem>
                  ))}
                  <WrapItem>
                    {!showCustomVibe ? (
                      <Button
                        size="sm"
                        variant="outline"
                        borderRadius="full"
                        px={4}
                        py={2}
                        onClick={() => setShowCustomVibe(true)}
                        borderColor="blue.300"
                        color="blue.600"
                        borderStyle="dashed"
                        _hover={{ borderColor: "blue.400", bg: "blue.50" }}
                      >
                        + Type my own vibe
                      </Button>
                    ) : (
                      <Input
                        placeholder="Your custom vibe..."
                        size="sm"
                        borderRadius="full"
                        value={customVibe}
                        onChange={(e) => setCustomVibe(e.target.value)}
                        onBlur={() => !customVibe && setShowCustomVibe(false)}
                        autoFocus
                        maxW="200px"
                      />
                    )}
                  </WrapItem>
                </Wrap>
                {selectedVibes.length >= 2 && (
                  <Text fontSize="xs" color="orange.600">
                    Maximum 2 vibes selected
                  </Text>
                )}
              </VStack>

              {/* Section C: Occasion Chips */}
              <VStack spacing={4} align="stretch">
                <Text fontSize="lg" fontWeight="semibold" color="gray.700">
                  Are you dressing for something?
                </Text>
                <Wrap spacing={3}>
                  {occasionOptions.map((occasion) => (
                    <WrapItem key={occasion}>
                      <ChipButton
                        label={occasion}
                        isSelected={selectedOccasion === occasion}
                        onClick={() => handleOccasionSelect(occasion)}
                      />
                    </WrapItem>
                  ))}
                  <WrapItem>
                    {!showCustomOccasion ? (
                      <Button
                        size="sm"
                        variant="outline"
                        borderRadius="full"
                        px={4}
                        py={2}
                        onClick={() => setShowCustomOccasion(true)}
                        borderColor="blue.300"
                        color="blue.600"
                        borderStyle="dashed"
                        _hover={{ borderColor: "blue.400", bg: "blue.50" }}
                      >
                        + Other Occasion
                      </Button>
                    ) : (
                      <Input
                        placeholder="Your custom occasion..."
                        size="sm"
                        borderRadius="full"
                        value={customOccasion}
                        onChange={(e) => setCustomOccasion(e.target.value)}
                        onBlur={() => !customOccasion && setShowCustomOccasion(false)}
                        autoFocus
                        maxW="200px"
                      />
                    )}
                  </WrapItem>
                </Wrap>
              </VStack>


              {/* Bottom Buttons */}
              <VStack spacing={2} pt={4}>
                <Button
                  size="lg"
                  colorScheme="blue"
                  borderRadius="full"
                  px={8}
                  py={3}
                  onClick={handleContinue}
                  w="100%"
                  maxW="300px"
                  _hover={{ transform: "translateY(-1px)", shadow: "lg" }}
                  transition="all 0.2s"
                >
                  Continue
                </Button>
                <Button
                  variant="ghost"
                  color="gray.600"
                  onClick={onSkip}
                  _hover={{ color: "blue.600" }}
                  rightIcon={<Text>→</Text>}
                >
                  I'll fill this later
                </Button>
              </VStack>
            </VStack>
          </Box>
        </motion.div>
      </VStack>
    </Box>
  );
};

export default IntentChipsScreen;